import { createContext, useContext } from 'react';

import { useQuery } from '@apollo/client';

import type { GetPropertyTypesQuery } from '../../../__generated__/graphql';
import { GET_PROPERTY_TYPES } from '../../../common/queries';
import { LoadingSpinner } from '../../../components/LoadingSpinner';

type PropertyType = GetPropertyTypesQuery['property_types'][number];

type PropertyTypesContextType = {
  getPropertyTypeLabel: (name: string | null) => string;
};

const PropertyTypesContext = createContext<PropertyTypesContextType | null>(
  null,
);

type PropertyTypesProviderProps = {
  children: React.ReactNode;
};

export const PropertyTypesProvider = ({
  children,
}: PropertyTypesProviderProps) => {
  const { data, loading } = useQuery(GET_PROPERTY_TYPES);
  const propertyTypes = data?.property_types;

  const getPropertyTypeLabel = (type: string | null) =>
    propertyTypes?.find((pt: PropertyType) => pt.name === type)?.label ??
    type ??
    '';

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <PropertyTypesContext.Provider value={{ getPropertyTypeLabel }}>
      {children}
    </PropertyTypesContext.Provider>
  );
};

export const usePropertyTypes = () => {
  const context = useContext(PropertyTypesContext);
  if (!context) {
    throw new Error(
      'Property types context not found - usePropertyTypes must be used within PropertyTypesProvider',
    );
  }
  return context;
};
